/**
 * Datadog browser error filters for the portal app (shared by RUM + Logs beforeSend).
 * Kept side-effect free so it can be unit tested without booting the app.
 */

/**
 * Non-breaking error patterns to suppress from Datadog error tracking.
 * These are known noise that do not impact user flow:
 *  - Script error / Empty reason (cross-origin or browser-internal noise)
 *  - ResizeObserver loop (benign browser warning when callback exceeds one frame)
 *  - Third-party tracking pixel errors (bat.bing, GA, GTM, doubleclick,
 *    Pinterest, Reddit, HubSpot, OpenAI pixels)
 *  - Browser interventions (touchmove preventDefault on passive listeners)
 *  - iOS in-app webview autofill bridge noise (_AutofillCallbackHandler)
 *  - dotLottie WASM streaming fallbacks (player falls back automatically)
 */
export const SUPPRESSED_ERROR_PATTERNS = [
    "Script error.",
    "Empty reason",
    "ResizeObserver loop",
    "bat.bing.com",
    "google-analytics.com",
    "googletagmanager.com",
    "doubleclick.net",
    "ct.pinterest.com",
    "alb.reddit.com",
    "api.hubapi.com",
    "bzrcdn.openai.com",
    "Ignored attempt to cancel",
    "_AutofillCallbackHandler",
    "wasm streaming compile failed",
    "falling back to ArrayBuffer"
];

export const isNonBreakingError = (message: string | undefined): boolean => {
    if (!message) return false;
    return SUPPRESSED_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

/**
 * Client-side connectivity failures (offline, aborted navigation, flaky
 * mobile networks). They are not application bugs, so the Logs beforeSend
 * downgrades them from error to warn instead of dropping them.
 */
export const NETWORK_ERROR_PATTERNS = [
    "ERR_NETWORK",
    "Possible local connectivity issue",
    "Network Error",
    "Failed to load",
    "Failed to fetch",
    "Load failed",
    "XHR error",
    "Fetch error"
];

export const isNetworkConnectivityError = (
    message: string | undefined
): boolean => {
    if (!message) return false;
    return NETWORK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};
